// date-fns supports Norwegian Bokmål
import { fi, sv, nb, enGB } from 'date-fns/locale';
import { format } from 'date-fns';
import useI18n from "./useI18n";
import useEnv from "./useEnv";

const useFormatter = () => {
    const { getLocale, getLocaleBCP47 } = useI18n();
    const { isDev } = useEnv();
    const localeBCP47 = getLocaleBCP47();

    /**
     * Format date and time to locale specific format.
     * Uses toLocaleString method, which should work for all locales, but if it fails, returns original string.
     * @param {string|Date} time
     * @returns {string}
     */
    const formatDateTime = (time) => {
        if (typeof time === 'string') {
            time = new Date(time);
        }
        try {
            return time.toLocaleString(
                localeBCP47.replace('_', '-'),
                { dateStyle: 'short', timeStyle: 'short' }
            );
        } catch (error) {
            if (isDev) {
                console.error('Error formatting date and time', error);
            }
            return typeof time === 'string' ? time : time.toString();
        }
    }

    /**
     * Format date to locale specific format.
     * @param {string|Date} date
     * @param {string|undefined} formatByCountry
     * @returns {string} - date in locale specific format
     */
    const formatDate = (date, formatByCountry = undefined) => {
        // format is not given as a parameter
        if (formatByCountry === undefined) {
            formatByCountry = getLocale();
        }

        // get correct localize property, use fi format as fallback
        switch (formatByCountry) {
            case 'se':
                formatByCountry = sv;
                break;
            case 'no':
                formatByCountry = nb;
                break;
            case 'en':
                formatByCountry = enGB;
                break;
            case 'fi':
            default:
                formatByCountry = fi;
                break;
        }

        // use date-fns pattern 'dd.MM.yyyy' for Finland
        // for other countries use date-fns pattern 'P' for formatting (long localized date)
        try {
            return format(date, formatByCountry === fi ? 'dd.MM.yyyy' : 'P', {locale: formatByCountry});
        } catch (error) {
            if (isDev) {
                console.error('Error formatting date, returning original string', error);
            }
            return typeof date === 'string' ? date : String(date);
        }
    }

    /**
     * Format time to locale specific format, show only hours and minutes.
     *
     * Can be given country as parameter, but if left undefined,
     * uses operative country from localstorage.
     *
     * @param {string|Date} time UTC timestamp
     * @param {string|undefined} formatByCountry
     * @returns string - time in locale specific format
     */
    const formatTime = (time, formatByCountry = undefined) => {
        if (formatByCountry === undefined) {
            formatByCountry = getLocale();
        }

        let locale;

        // get correct localize property, use fi format as fallback
        switch (formatByCountry) {
            case 'se':
                locale = sv;
                break;
            case 'no':
                locale = nb;
                break;
            case 'en':
                locale = enGB;
                break;
            case 'fi':
            default:
                locale = fi;
                break;
        }

        return format(time, 'p', { locale });
    }

    /**
     * Format money value to locale specific format.
     * @param {number|null|undefined} value
     * @param {string} currency
     * @param {Intl.LocalesArgument|undefined|null} locale
     * @returns {string}
     */
    const formatCurrency = (value, currency, locale = undefined) => {
        if (value === undefined || value === null) {
            return '';
        }

        if (locale === undefined || locale === null) {
            locale = getLocaleBCP47().replace('_', '-');
        }

        // Currency has to be three-letter code, otherwise use fallback (usually env variable value)
        if (currency?.length === 3) {
            return value.toLocaleString(locale, {
                style: 'currency',
                currency: currency.toUpperCase()
            });
        }

        return value.toString();
    }

    return {
        formatDate,
        formatTime,
        formatDateTime,
        formatCurrency
    }
}

export default useFormatter;
